// collector/lineView.js
//
// Pure view helpers for the collector's line card, detail sheet, shelf-report
// sheet and «خلصت المحل» sheet (collector-app P1-D). Like dayView.js they only
// READ the day document (backend brief 2026-09-30-collector-day-read) and never
// decide a state the backend owns: what a row can do comes from the line's own
// flags (`canMark`, `canReopen`, `leaderState`, `activeQuestion`).
//
// Copy stays in the `collector.*` locale; these helpers return keys + params or
// plain values, so they are testable without a locale.
import { formatClock, isReleased } from '@/collector/dayView.js';

// ── Folding a shop's lines into cards ─────────────────────────────────────────

/**
 * A line the collector can still act on or must still see as live: not the
 * leader's skip / cancel, and not one moved to another collector (`movedAway`
 * is set locally from an action's `current.assignedToMe === false`).
 */
export function isLiveLine(line) {
  return !isReleased(line) && !line?.movedAway;
}

/**
 * Fold a shop's lines into the cards the page draws, in first-appearance order:
 *  - lines sharing a `groupKey` fold into ONE `group` card (shape A) when two or
 *    more are live; a lone live line is a `single` card (shape B / C);
 *  - a released or moved line is never folded — it keeps its place as its own
 *    `dashed` card («لاغي» / «اتنقل … مش عليك»), so nothing the leader did to a
 *    line disappears from the shop.
 *
 * @param {Array<object>} lines - a shop's `lines[]`
 * @returns {Array<{ kind: 'group'|'single'|'dashed', key: string, lines: object[] }>}
 */
export function foldLines(lines) {
  const cards = [];
  const byKey = new Map();
  for (const line of Array.isArray(lines) ? lines : []) {
    if (!isLiveLine(line)) {
      cards.push({ kind: 'dashed', key: `dashed-${line.id}`, lines: [line] });
      continue;
    }
    const key = line.groupKey ?? `line-${line.id}`;
    let card = byKey.get(key);
    if (!card) {
      card = { kind: 'single', key: `card-${key}`, lines: [] };
      byKey.set(key, card);
      cards.push(card);
    }
    card.lines.push(line);
    if (card.lines.length > 1) card.kind = 'group';
  }
  return cards;
}

// ── What a row can do ─────────────────────────────────────────────────────────

/**
 * The row's state, read from the backend's own flags. `canMark` alone decides
 * whether the two mark buttons show; a line parked on a member question hides
 * them (the status machine refuses a mark there) and says why.
 *
 * @returns {'moved'|'released'|'verified'|'marked'|'question'|'markable'|'locked'}
 */
export function lineState(line) {
  if (line?.movedAway) return 'moved';
  if (isReleased(line)) return 'released';
  if (line?.leaderState?.verified) return 'verified';
  if (line?.outcome === 'collected' || line?.outcome === 'out_of_stock') return 'marked';
  if (line?.activeQuestion || line?.outcome === 'issue') return 'question';
  if (line?.canMark) return 'markable';
  return 'locked';
}

/** The leader rejected my mark and the line is back to pending. */
export function isRejected(line) {
  return line?.outcome === 'pending' && line?.leaderState?.rejectedReason != null;
}

/** Lines of a card that still take a mark right now. */
export function markableLines(lines) {
  return (lines ?? []).filter((line) => lineState(line) === 'markable');
}

// ── Photos ────────────────────────────────────────────────────────────────────

/** Tier → the label key the photo wears (the API's own `label` is generic). */
const TIER_LABEL = {
  member: 'collector.photoTier.member',
  post: 'collector.photoTier.post',
  catalog: 'collector.photoTier.catalog',
  collector: 'collector.photoTier.collector',
  brief: 'collector.photoTier.brief',
  answer: 'collector.photoTier.answer',
};

/**
 * A line's photos for the card and the browser: HER photo first (it is what she
 * wants), then the API's own order (brief, answer, member, catalog, post,
 * collector). Entries without a url are dropped.
 */
export function orderedPhotos(line) {
  const photos = (Array.isArray(line?.photos) ? line.photos : []).filter((photo) => photo?.url || photo?.thumbUrl);
  const hers = photos.filter((photo) => photo.tier === 'member');
  return [...hers, ...photos.filter((photo) => photo.tier !== 'member')];
}

/** The photos of the whole card, deduplicated by url (a group shares its post photos). */
export function groupPhotos(lines) {
  const seen = new Set();
  const out = [];
  for (const line of lines ?? []) {
    for (const photo of orderedPhotos(line)) {
      if (photo.tier === 'member') continue; // hers belong to her row, not the group strip
      const key = photo.url ?? photo.thumbUrl;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(photo);
    }
  }
  return out;
}

export function photoThumb(photo) {
  return photo?.thumbUrl ?? photo?.url ?? null;
}

/**
 * The picture for the detail sheet's big slide: the 320 px version when the
 * photo has one (the morning download keeps it for the first photo of every
 * line), else the original. The original is for pinch-zoom only.
 */
export function photoMedium(photo) {
  return photo?.mediumUrl ?? photo?.url ?? photo?.thumbUrl ?? null;
}

/**
 * The caption chip over a photo in the browser: «صورة العميلة — «زي دي
 * بالظبط»» (her own words go on HER photo), «البوست», «رف امبارح», «صورة
 * المقر», «صورة الرد».
 *
 * @returns {{ key: string, params?: object }}
 */
export function photoCaption(photo, line) {
  if (photo?.tier === 'member' && line?.memberNote) {
    return { key: 'collector.photoTier.memberWithNote', params: { note: line.memberNote } };
  }
  return { key: TIER_LABEL[photo?.tier] ?? TIER_LABEL.post };
}

/**
 * The short tag printed on a card thumbnail: «صورة العميلة» / «صورتها» for hers,
 * «البوست» / «بوست» otherwise.
 * @param {'long'|'short'} [size]
 */
export function thumbTag(photo, size = 'long') {
  const member = photo?.tier === 'member';
  if (size === 'short') return member ? 'collector.thumb.hers' : 'collector.thumb.post';
  return member ? 'collector.photoTier.member' : 'collector.photoTier.post';
}

/** The short tag of the tiers that have one; the others wear their `photoTier` name. */
const TIER_TAG_SHORT = {
  member: 'collector.thumb.hers',
  brief: 'collector.thumb.hq',
  post: 'collector.thumb.post',
};

/**
 * The tag printed on a card thumbnail, by the photo's REAL tier (collector-
 * clarity, rule G) — a leader's photo is never tagged as a post photo, and a
 * shelf or answer photo says what it is. `thumbTag` knows only "hers or post";
 * this replaces it as each card stops calling it.
 * @param {'long'|'short'} [size]
 */
export function tierTag(photo, size = 'long') {
  const tier = TIER_LABEL[photo?.tier] ? photo.tier : 'post';
  return size === 'short' ? (TIER_TAG_SHORT[tier] ?? TIER_LABEL[tier]) : TIER_LABEL[tier];
}

/**
 * The shared strip of a grouped card: `groupPhotos` without her own photos and
 * without the leader's instruction photos. Those two are about ONE member's
 * line, so they are drawn on her row (`lineInstructions`) and never here — a
 * leader's photo leading the strip was read as «صورة البوست 1».
 */
export function stripPhotos(lines) {
  return groupPhotos(lines).filter((photo) => photo.tier !== 'member' && photo.tier !== 'brief');
}

/** How many photos a strip hides behind «+N». */
export function hiddenPhotoCount(total, shown) {
  return Math.max(0, (Number(total) || 0) - (Number(shown) || 0));
}

/** Index in the browser counter: «1 / 12». */
export function photoCounter(index, total) {
  return `${Math.min(total, Math.max(0, index) + 1)} / ${total}`;
}

// ── Words on the card ─────────────────────────────────────────────────────────

/** Her colour word — the ordered variant's name — or null (→ «أي لون»). */
export function colourWord(line) {
  const word = String(line?.variantName ?? '').trim();
  return word === '' ? null : word;
}

/**
 * Her note whole, with her colour word picked out in bold: the pieces are
 * rendered as text (no HTML), so a note can never inject markup.
 * @returns {Array<{ text: string, bold: boolean }>}
 */
export function noteSegments(note, word) {
  const text = String(note ?? '');
  if (text === '') return [];
  const needle = String(word ?? '').trim();
  if (needle === '') return [{ text, bold: false }];
  const at = text.indexOf(needle);
  if (at < 0) return [{ text, bold: false }];
  return [
    { text: text.slice(0, at), bold: false },
    { text: needle, bold: true },
    { text: text.slice(at + needle.length), bold: false },
  ].filter((segment) => segment.text !== '');
}

/** Pieces ordered on a grouped card: the total of the live lines. */
export function groupTotals(lines) {
  const live = (lines ?? []).filter(isLiveLine);
  return {
    qty: live.reduce((sum, line) => sum + (Number(line.qty) || 0), 0),
    members: live.length,
  };
}

/**
 * What I reported from the shelf on this line (this try only): present while
 * the line is still unresolved — pending, or parked on the member question my
 * report started (`issue`). Once marked, the chip carries the facts.
 * `options` are the colour chips he picked («متاح بدل كده», `collectorFacts.options`).
 * @returns {{ at: string, note: string|null, options: string[], foundQty: number|null, priceCents: number|null, hasPhotos: boolean } | null}
 */
export function reportSummary(line) {
  const facts = line?.collectorFacts;
  if (!facts?.reportedAt || (line?.outcome !== 'pending' && line?.outcome !== 'issue')) return null;
  return {
    at: facts.reportedAt,
    note: facts.note ? String(facts.note) : null,
    options: reportOptionLabels(facts.options),
    foundQty: facts.foundQty ?? null,
    priceCents: facts.priceCents ?? null,
    hasPhotos: Array.isArray(facts.photoUrls) && facts.photoUrls.length > 0,
  };
}

// ── Her answer, live (collector-app P3-C, L-E / L-F) ──────────────────────────

/**
 * The words the backend opens the note of an «اشتريته» / «مش لقيته» report with
 * (CollectorActionApplier::reportOnAnswer: «اشتراه: {her option} — {his words}»).
 * The read has no structured field for the report yet (FE→BE brief
 * 2026-10-01-collector-report-on-answer-kind), so after a read the note's
 * opening words are how the card knows what he reported. A structured
 * `collectorFacts.answerReport` wins when the backend sends it — the overlay
 * sets that same key while the tap is still local.
 */
const ANSWER_REPORT_WORDS = Object.freeze({ bought: 'اشتراه', not_found: 'مش لقاه' });

/**
 * What I reported on her answer: `'bought'`, `'not_found'`, or null (nothing
 * yet). The line's LATEST report decides — the backend keeps one current note.
 * @returns {'bought'|'not_found'|null}
 */
export function answerReportKind(line) {
  const facts = line?.collectorFacts;
  if (facts?.answerReport === 'bought' || facts?.answerReport === 'not_found') return facts.answerReport;
  const note = String(facts?.note ?? '').trimStart();
  if (note === '') return null;
  for (const [kind, words] of Object.entries(ANSWER_REPORT_WORDS)) {
    if (note.startsWith(words)) return kind;
  }
  return null;
}

/**
 * The member question a line is parked on, as the collector reads it: the
 * leader asked and she has not answered (`answered: false`), or she answered
 * (`answered: true`) — her words, the server's `instruction` (printed as sent,
 * never rebuilt here) and what I have reported on it. Null when no question is
 * live. `canReport`: only a YES has anything to buy or not find; «لأ» and
 * «استنى» say what to do and need no tap.
 *
 * @returns {{ answered: false, sentAt: string|null } | { answered: true, response: string|null, at: string|null, words: string|null, instruction: string|null, canReport: boolean, reported: 'bought'|'not_found'|null } | null}
 */
export function answerView(line) {
  const question = line?.activeQuestion;
  if (!question) return null;
  if (!question.answered) return { answered: false, sentAt: question.sentAt ?? null };
  const response = question.response ?? null;
  const words = String(question.note ?? '').trim();
  return {
    answered: true,
    response,
    at: question.respondedAt ?? null,
    words: words === '' ? null : words,
    instruction: question.instruction ? String(question.instruction) : null,
    canReport: response === 'yes',
    reported: answerReportKind(line),
  };
}

/** «{her words}» — {instruction}, whichever of the two exist. */
export function answerSaid(view) {
  return [view?.words ? `«${view.words}»` : null, view?.instruction ?? null].filter(Boolean).join(' — ');
}

/**
 * «ردّت 11:31: «أي لون» — هات أسود بـ 135» as a locale key and its params.
 * @returns {{ key: string, params: { time?: string, what: string } }}
 */
export function answerSaidPhrase(view) {
  const time = formatClock(view?.at);
  const what = answerSaid(view);
  return time
    ? { key: 'collector.answer.said', params: { time, what } }
    : { key: 'collector.answer.saidNoTime', params: { what } };
}

/**
 * The one LOUD line under a member's row on a grouped card, in priority order:
 * the leader's rejection, a live question (or her answer to it), a reservation with a deposit (money), what I reported, the
 * answer she already gave, else her note. `memberAnswer` is the last RESOLVED
 * answer — history — so it only speaks when she agreed.
 *
 * @returns {{ kind: 'rejected'|'question'|'answered'|'reserved'|'reported'|'agreed'|'note'|'none', text?: string }}
 */
export function memberRowFact(line) {
  if (isRejected(line)) return { kind: 'rejected', text: String(line.leaderState.rejectedReason ?? '') };
  if (line?.activeQuestion) return { kind: answerView(line)?.answered ? 'answered' : 'question' };
  if (line?.reservedForName) return { kind: 'reserved' };
  if (reportSummary(line)) return { kind: 'reported' };
  const answer = line?.memberAnswer;
  if (answer?.response === 'yes') {
    const text = answer.text ?? answer.optionLabel ?? answer.note ?? null;
    if (text) return { kind: 'agreed', text: String(text) };
  }
  if (line?.memberNote) return { kind: 'note', text: String(line.memberNote) };
  return { kind: 'none' };
}

// ── Rule G — her photo, her comment, the leader's notes (collector-clarity) ───

function wholeText(value) {
  const text = String(value ?? '').trim();
  return text === '' ? null : text;
}

/**
 * What a line carries that the collector must not miss: HER photos, her
 * comment, the leader's note and the leader's instruction photos. Each has its
 * own place on every card shape; nothing else on the line ever replaces one.
 * Empty arrays and nulls when absent.
 *
 * @returns {{ herPhotos: object[], note: string|null, brief: string|null, briefPhotos: object[] }}
 */
export function lineInstructions(line) {
  const photos = orderedPhotos(line);
  return {
    herPhotos: photos.filter((photo) => photo.tier === 'member'),
    note: wholeText(line?.memberNote),
    brief: wholeText(line?.leaderBrief),
    briefPhotos: photos.filter((photo) => photo.tier === 'brief'),
  };
}

/** The line carries any of the four: her photo, her comment, a leader note, a leader photo. */
export function hasInstructions(line) {
  const { herPhotos, note, brief, briefPhotos } = lineInstructions(line);
  return herPhotos.length > 0 || note != null || brief != null || briefPhotos.length > 0;
}

/**
 * The rows «اتجاب الباقي كله» marks: the PLAIN ones only (owner decision 5). A
 * row that carries instructions has to be read, so it is marked on its own.
 */
export function sweepableLines(lines) {
  return markableLines(lines).filter((line) => !hasInstructions(line));
}

/**
 * The status line of a member's row once her comment has a block of its own:
 * `memberRowFact` without its `note` kind. The other messages (rejection,
 * question, answer, reservation, report, her earlier yes) STACK with her
 * comment instead of replacing it.
 *
 * @returns {{ kind: 'rejected'|'question'|'answered'|'reserved'|'reported'|'agreed'|'none', text?: string }}
 */
export function rowStatusFact(line) {
  const fact = memberRowFact(line);
  return fact.kind === 'note' ? { kind: 'none' } : fact;
}

// ── Earlier tries ─────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

function cairoDay(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // en-CA prints YYYY-MM-DD; the day boundary is Cairo's, not the phone's.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo' }).format(date);
}

/**
 * Whole days between a date (or timestamp) and `now`, by the Cairo calendar.
 * Null when the date cannot be read.
 */
export function daysAgo(date, now = new Date()) {
  const then = cairoDay(String(date).length === 10 ? `${date}T12:00:00+03:00` : date);
  const today = cairoDay(now);
  if (!then || !today) return null;
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${then}T00:00:00Z`)) / DAY_MS);
}

/**
 * When a try happened: «امبارح» / «النهارده» / «28 سبتمبر» (the plan date style).
 * @returns {{ key: string, params?: object } | { date: string }}
 */
export function tryDay(date, now = new Date()) {
  const days = daysAgo(date, now);
  if (days === 0) return { key: 'collector.tries.today' };
  if (days === 1) return { key: 'collector.tries.yesterday' };
  const when = new Date(`${String(date).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(when.getTime())) return { key: 'collector.tries.earlier' };
  return {
    date: new Intl.DateTimeFormat('ar-EG-u-nu-latn', { timeZone: 'UTC', day: 'numeric', month: 'long' }).format(when),
  };
}

/** The outcome word of an earlier try. */
export function tryOutcomeKey(outcome) {
  switch (outcome) {
    case 'collected':
      return 'collector.outcome.collected';
    case 'out_of_stock':
      return 'collector.outcome.out_of_stock';
    case 'issue':
      return 'collector.outcome.issue';
    default:
      return 'collector.outcome.pending';
  }
}

/** What she said to an earlier try: «العضوة وافقت» / «العضوة رفضت البديل» / «العضوة طلبت تستنى». */
export function memberAnswerKey(answer) {
  if (!answer?.response) return null;
  if (answer.response === 'yes') return 'collector.tries.answerYes';
  if (answer.response === 'no') return 'collector.tries.answerNo';
  return 'collector.tries.answerWait';
}

/** The count word for «من N أيام» on an open out-of-stock mark. */
export function openMarkDays(since, now = new Date()) {
  const days = daysAgo(since, now);
  return days == null ? null : Math.max(0, days);
}

// ── «بلّغ من الرف» ────────────────────────────────────────────────────────────

/** Most shelf photos a line holds for one try (the backend refuses more, `photo_cap`). */
export const SHELF_PHOTO_CAP = 5;

/** The chips every report offers next to the catalogue's colour words. */
export const FIXED_REPORT_CHIPS = Object.freeze(['another', 'tomorrow', 'reserved']);

/**
 * The backend's own caps on `facts.options[]` (CollectionPlanItem::REPORTED_OPTIONS_MAX
 * and REPORTED_OPTION_LABEL_MAX): a longer list or label is a 422 for the WHOLE
 * batch, so the sheet stops at them instead of finding out.
 */
export const REPORT_OPTIONS_MAX = 6;
export const REPORT_OPTION_MAX_CHARS = 60;

/**
 * Colour labels the way the server will keep them: trimmed, blanks dropped,
 * de-duplicated case-insensitively (first spelling wins), at most
 * REPORT_OPTIONS_MAX of at most REPORT_OPTION_MAX_CHARS characters.
 * @param {unknown} labels
 * @returns {string[]}
 */
export function reportOptionLabels(labels) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(labels) ? labels : []) {
    const label = Array.from(String(raw ?? '').trim()).slice(0, REPORT_OPTION_MAX_CHARS).join('');
    const key = label.toLowerCase();
    if (label === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(label);
    if (out.length === REPORT_OPTIONS_MAX) break;
  }
  return out;
}

/** The same colours, ignoring order and case. */
function sameOptions(a, b) {
  const left = reportOptionLabels(a).map((label) => label.toLowerCase()).sort();
  const right = reportOptionLabels(b).map((label) => label.toLowerCase()).sort();
  return left.length === right.length && left.every((label, index) => label === right[index]);
}

/** Room left for new shelf photos on this line. */
export function shelfPhotoRoom(line) {
  const held = Array.isArray(line?.collectorFacts?.photoUrls) ? line.collectorFacts.photoUrls.length : 0;
  return Math.max(0, SHELF_PHOTO_CAP - held);
}

/**
 * Western-digit money typed by a collector → cents. Takes Arabic-Indic digits
 * too (a phone keyboard can be in either), a comma or Arabic comma as the
 * thousands mark, and up to two decimals. Null when it is not an amount.
 *
 * @param {string|number|null} input - pounds as typed
 * @returns {number|null}
 */
export function parsePoundsToCents(input) {
  if (input == null) return null;
  let text = String(input)
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[,،\s]/g, '')
    .replace(/٫/g, '.');
  if (text === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  text = Number(text).toFixed(2);
  return Math.round(Number(text) * 100);
}

/** A whole count typed by a collector (either digit set) → a number ≥ 0, else null. */
export function parseWholeNumber(input) {
  if (input == null) return null;
  const text = String(input)
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/\s/g, '');
  return /^\d{1,4}$/.test(text) ? Number(text) : null;
}

/** Cents → the text a price input starts with: «320», «320.5». */
export function centsToInputText(cents) {
  if (cents == null || Number.isNaN(Number(cents))) return '';
  const pounds = Number(cents) / 100;
  return Number.isInteger(pounds) ? String(pounds) : String(Number(pounds.toFixed(2)));
}

/**
 * The ONE `facts` tap «ابعت للّيدر» sends (brief: `facts {foundQty?, priceCents?,
 * photoIds?[], options?[], note?}`): the found quantity and the shelf price as
 * they stand, the ids the photo uploads returned, the colours he picked, and
 * the note.
 *
 * The chips split in two (L-B): a colour word is an OPTION and travels as
 * `options[]`; the three fixed chips («شكل تاني», «جاي بكره», «محجوز باسمي»)
 * are statements, so they go into the note text — never into `options`. The
 * note is the statements, then his own words.
 *
 * The latest `options` list REPLACES the stored one, so it is sent whenever
 * colours are picked — and as `[]` when he took off every colour the leader
 * already held (`heldOptions`); with nothing picked and nothing held the key
 * stays out and the stored list is left alone.
 *
 * @param {{ foundQty: number|null, priceCents: number|null, photoIds: string[], chips: string[], fixedChips: string[], heldOptions: string[], text: string }} draft
 */
export function factsPayload({ foundQty, priceCents, photoIds = [], chips = [], fixedChips = [], heldOptions = [], text = '' }) {
  const fixed = new Set(fixedChips);
  const options = reportOptionLabels(chips.filter((chip) => !fixed.has(chip)));
  const statements = chips.filter((chip) => fixed.has(chip));
  const payload = {};
  if (foundQty != null) payload.foundQty = foundQty;
  // 0 is not a price: the backend refuses `priceCents` below 1, for the whole report.
  if (priceCents != null && priceCents > 0) payload.priceCents = priceCents;
  if (photoIds.length) payload.photoIds = [...photoIds];
  if (options.length > 0 || reportOptionLabels(heldOptions).length > 0) payload.options = options;
  const note = [statements.join('، '), String(text).trim()].filter((part) => part !== '').join(' · ');
  if (note !== '') payload.note = note;
  return payload;
}

/**
 * Whether a report says anything the leader does not already have: a report
 * equal to the prefill (the ordered quantity, the channel price, the colours
 * he already reported) with no photo, statement chip or words is noise, not a
 * report. Taking a held colour off IS content — it changes what the leader sees.
 *
 * @param {{ foundQty: number|null, priceCents: number|null, photoIds: string[], chips: string[], fixedChips: string[], text: string }} draft
 * @param {{ foundQty: number, priceCents: number|null, options?: string[] }} prefill
 */
export function reportHasContent(draft, prefill) {
  if ((draft.photoIds?.length ?? 0) > 0) return true;
  const fixed = new Set(draft.fixedChips ?? []);
  const chips = draft.chips ?? [];
  if (chips.some((chip) => fixed.has(chip))) return true;
  if (!sameOptions(chips.filter((chip) => !fixed.has(chip)), prefill.options ?? [])) return true;
  if (String(draft.text ?? '').trim() !== '') return true;
  if (draft.foundQty != null && draft.foundQty !== prefill.foundQty) return true;
  if (draft.priceCents != null && draft.priceCents !== prefill.priceCents) return true;
  return false;
}

// ── «خلصت المحل» ──────────────────────────────────────────────────────────────

/**
 * The lines the done sheet asks about: pending, not parked on a member
 * question (no mark is possible there), still mine. A shop the collector
 * already closed asks nothing more.
 */
export function unmarkedLines(shop) {
  if (shop?.state === 'closed_by_collector') return [];
  return (shop?.lines ?? []).filter(
    (line) => isLiveLine(line) && line.outcome === 'pending' && !line.activeQuestion,
  );
}

/**
 * Invoice vs my marks — the live line on the done sheet. `difference` is
 * invoice − spent (the backend's `invoice.differenceCents`): negative = the
 * paper is less than my marks. Null until an invoice amount is typed.
 *
 * @param {number|null} invoiceCents
 * @param {number} spentCents - `shop.spentCents` (net of deposits)
 * @returns {{ matched: boolean, differenceCents: number } | null}
 */
export function invoiceDifference(invoiceCents, spentCents) {
  if (invoiceCents == null) return null;
  const differenceCents = invoiceCents - (Number(spentCents) || 0);
  return { matched: differenceCents === 0, differenceCents };
}

/**
 * «الكاش معاك بعد المحل»: what I hold once the shop is filed. The day's
 * `withMeCents` counts this shop at its marks (`spentCents`); the paper is what
 * I really paid, so the cash moves by the difference. With no amount (empty, or
 * «المحل مادّاش فاتورة») the marks stand.
 *
 * @param {number} withMeCents - `cash.withMeCents` right now
 * @param {number|null} invoiceCents
 * @param {number} spentCents
 */
export function cashAfterShop(withMeCents, invoiceCents, spentCents) {
  const base = Number(withMeCents) || 0;
  if (invoiceCents == null) return base;
  return base + (Number(spentCents) || 0) - invoiceCents;
}
